import React, {Component} from 'react'
import {get, sum, values, isFinite} from 'lodash/fp'
import {scaleLinear} from 'd3-scale'
import ReactCSSTransitionGroup from 'react-addons-css-transition-group'

import {questions} from './data/questions'
import {getUserCo2} from './scoring'

import Page from './components/Page'
import NonUS from './apps/NonUS'
import Questionnaire from './apps/Questionnaire'
import Results from './apps/Results'
import Payment from './apps/Payment'
import About from './apps/About'

import './styles.css'

const topColor = scaleLinear()
  .domain([2.5, 7.5, 10.0, 12.5, 17.5])
  .range(['#247ac1', '#543cae', '#7e369c', '#9f3779', '#c12424'])
  .clamp(true)

const bottomColor = scaleLinear()
  .domain([2.5, 7.5, 10.0, 12.5, 17.5])
  .range(['#123c5f', '#291d55', '#3e1b4d', '#4e1b3c', '#5f1212'])
  .clamp(true)

const getPageGradient = emissions => {
  const co2 = sum(values(emissions))
  return `linear-gradient(to bottom, ${topColor(co2)}, ${bottomColor(co2)})`
}

const defaultAnswer = question => question.type === 'number'
  ? question.answers.map(() => "")
  : undefined

const OLD_IDX_LOC = 'carbotax-answers-idx-v1'
const OLD_ANSWERS_LOC = 'carbotax-answers-v1'
const FINISHED_LOC = 'carbotax-finished-v1'
const COUNTRY_LOC = 'carbotax-country-v1'
const setLS = (loc, val) => {
  if (window.localStorage) {
    try {
      window.localStorage.setItem(loc, val)
    } catch(e) {}
  }
}

const getLS = loc => {
  if (window.localStorage) {
    try {
      return window.localStorage.getItem(loc)
    } catch(e) {}
  }
}

const retrieveOldAnswerIdx = () => {
  const idx = parseInt(getLS(OLD_IDX_LOC), 10)
  return isFinite(idx) ? idx : undefined
}

const retrieveOldAnswers = () => {
  const old = getLS(OLD_ANSWERS_LOC)
  return old ? JSON.parse(old) : questions.map(defaultAnswer)
}

const retrieveSavedProgress = () => {
  const old = getLS(FINISHED_LOC)
  const finished = old ? JSON.parse(old) : false
  return finished ? 'result' : 'quiz'
}

const retrieveSavedCountry = () => {
  const old = getLS(COUNTRY_LOC)
  return old ? old : 'US'
}

const trackCo2 = co2 => {
  if (window.ga) {
    window.ga('send', {
      hitType: 'event',
      eventCategory: 'Quiz',
      eventAction: 'finish',
      eventLabel: 'co2',
      eventValue: co2,
    })
  }
}

const trackCountry = country => {
  if (window.ga) {
    window.ga('send', {
      hitType: 'event',
      eventCategory: 'Quiz',
      eventAction: 'finish',
      eventLabel: country,
    })
  }
}

class App extends Component {
  // progress is one of ['nonUS', 'quiz', 'result', 'payment', 'paymentJoint']
  state = {
    answers: retrieveOldAnswers(),
    emissions: [],
    progress: retrieveSavedProgress(),
    gradient: 'linear-gradient(to bottom, #933178, #4d2140)',
    country: retrieveSavedCountry(),
    direction: 'app-slide',
    overlay: false,
  }

  // TODO(jong): merge questions and answers data structure?
  componentWillMount() {
    const emissions = getUserCo2(questions, this.state.answers)
    this.setState({emissions, gradient: getPageGradient(emissions)})
  }

  onAnswersChange = answers => {
    const emissions = getUserCo2(questions, answers)
    this.setState({
      answers,
      emissions,
      gradient: getPageGradient(emissions),
    })

    setLS(OLD_ANSWERS_LOC, JSON.stringify(answers))
  }

  onFinished = () => {
    const {emissions} = this.state
    this.setState({progress: 'result', direction: 'app-slide'})
    setLS(FINISHED_LOC, "true")
    trackCo2(sum(values(emissions)))
  }
  
  onCountrySelected = country => {
    this.setState({country, progress: 'result', direction: 'app-slide'})
    setLS(FINISHED_LOC, "true")
    setLS(COUNTRY_LOC, country)
    trackCountry(country)  
  }

  onNavigate = idx => {
    setLS(OLD_IDX_LOC, idx)
  }

  onReviseAnswers = () => {
    this.setState({progress: 'quiz', direction: 'app-slide-reverse'})
    setLS(FINISHED_LOC, "false") 
  }

  onReviseCountry = () => {
    this.setState({progress: 'nonUS', direction: 'app-slide-reverse'})
    setLS(FINISHED_LOC, "false") 
    setLS(COUNTRY_LOC, "")
  }
  
  onAcknowledge = () => this.setState({progress: 'payment', direction: 'app-slide'})
  onNonUS = () => this.setState({progress: 'nonUS', direction: 'app-slide'})
  onJointFiling = () => this.setState({progress: 'paymentJoint', direction: 'app-slide'})
  onSingleFiling = () => this.setState({progress: 'payment', direction: 'app-slide-reverse'})
  toggleOverlay = () => this.setState({
    overlay: !this.state.overlay,
    direction: this.state.overlay ? 'app-slide-reverse' : 'app-slide',
  })

  render() {
    const {answers, emissions, progress, gradient, direction, overlay, country} = this.state
    const personCount = get([1, 'value'], answers) 
    return (
      <Page style={{background: gradient}} onAboutClicked={this.toggleOverlay} about={overlay}>
        <ReactCSSTransitionGroup
          component='div'
          transitionName={direction}
          transitionEnterTimeout={200}
          transitionLeaveTimeout={200}
        >
          <div className="app-carousel" key={`${progress}-${overlay}`}>
            { !overlay && progress === 'nonUS' && <NonUS onCountrySelected={this.onCountrySelected} /> }
            { !overlay && progress === 'quiz' && <Questionnaire
                questions={questions}
                answers={answers}
                emissions={emissions}
                onNavigate={this.onNavigate}
                onAnswersChange={this.onAnswersChange}
                onFinished={this.onFinished}
                onNonUS={this.onNonUS}
                defaultProgress={retrieveOldAnswerIdx()}
              />
            }
            { !overlay && progress === 'result' &&
                <Results
                  country={country}
                  emissions={emissions}
                  onAcknowledge={this.onAcknowledge}
                  onJointAcknowledge={this.onJointFiling}
                  onReviseAnswers={this.onReviseAnswers}
                  onReviseCountry={this.onReviseCountry}
                  showJoint={personCount && personCount !== "1"}
                /> 
            }
            { overlay && <About /> }
          </div>
        </ReactCSSTransitionGroup>
      </Page>
    )
  }
}

export default App



// WEBPACK FOOTER //
// ./src/App.js